/** Signaling messages exchanged over the WebSocket relay. */
export type SignalMessage =
  // Client → DO, sent once on WS open. The DO uses this to detect when
  // both peers are present (rather than racing the 101 upgrade).
  | { type: 'hello' }
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit }
  | { type: 'peer-absent' }
  // DO → both clients when both have said hello. Host uses this as the
  // trigger to create its WebRTC offer.
  | { type: 'peer-ready' }
  | { type: 'peer-left' };

export type Role = 'host' | 'guest';

/**
 * Discrete physics events emitted by `step()` — fed into the animation
 * pipeline. The host broadcasts the buffered events alongside each state
 * snapshot so the guest can spawn the same effects locally.
 */
/** Optional flair tag for a goal — drives a special banner + effect. */
export type GoalTag =
  | 'own'
  | 'shutout'
  | 'hat-trick'
  | 'snipe'
  | 'comeback'
  | 'ace'
  | 'dunk'
  | 'speedrun'
  | 'long'
  | 'banger'
  | 'toilet'
  | 'top-shelf'
  | 'bank';

export type PhysicsEvent =
  | { e: 'hit'; paddle: 'host' | 'guest'; x: number; y: number; speed: number }
  | { e: 'wall'; x: number; y: number; horizontal: boolean; speed: number }
  // Puck reached a goal mouth. `x, y` is where the puck was AT THE MOMENT
  // of scoring (i.e., on the wall, in the goal opening) — the explosion
  // fires there. `tag` may add flair (long shot, ace, etc.).
  | { e: 'goal'; scorer: 'host' | 'guest'; x: number; y: number; tag?: GoalTag }
  // Stalemate detector teleported the puck. `fromX/Y` is where it was
  // trapped, `toX/Y` is the spawn point.
  | { e: 'reset'; fromX: number; fromY: number; toX: number; toY: number };

/** Game messages over the WebRTC DataChannel. */
export type GameMessage =
  // Guest → Host: paddle target in rink coordinates.
  | { t: 'input'; x: number; y: number; seq: number }
  // Host → Guest: authoritative snapshot.
  | {
      t: 'state';
      tick: number;
      puck: { x: number; y: number; vx: number; vy: number };
      hostPaddle: { x: number; y: number };
      guestPaddle: { x: number; y: number };
      score: { host: number; guest: number };
      // 'play' | 'goal-host' | 'goal-guest' | 'serve'
      phase: 'play' | 'goal-host' | 'goal-guest' | 'serve';
      /** Events that occurred between this snapshot and the previous one. */
      events?: PhysicsEvent[];
      /** Optional pause state — omitted when false to save bytes. */
      paused?: boolean;
    }
  // Host → Guest: match setup (one-shot, sent right after datachannel open).
  // Tells the guest which modifiers are active so it can reconstruct the
  // matching PhysicsConfig. Guest applies on receipt; default is Classic
  // if init never arrives.
  | { t: 'init'; modifierIds: string[] }
  // Either side: ping for RTT.
  | { t: 'ping'; ts: number }
  | { t: 'pong'; ts: number };

export const RINK = {
  width: 800,
  height: 500,
  goalHalfHeight: 80, // gap height at each end / 2
  puckRadius: 17,
  paddleRadius: 26,
  friction: 0.998, // per tick at 60Hz
  maxPuckSpeed: 18,
  // Rounded corners (quarter-circle arcs) replace the 90° rink corners
  // so the puck can't wedge into a right-angle trap. Picked comfortably
  // bigger than the puck's diameter (34) so a glancing puck deflects
  // cleanly. The arc is centered `cornerRadius` units in from both
  // adjacent walls (inscribed quarter-circle).
  cornerRadius: 50,
  // Goal pocket is a real physical chamber that extends past the rink
  // by this many world units. The puck enters through the goal mouth,
  // bounces off the pocket's top/bottom walls, and only scores when it
  // crosses the back wall. Combined with point-collision goal posts at
  // the mouth, this gives the air-hockey feel where weak/glancing shots
  // can rattle off the posts or stall short of the back.
  pocketDepth: 80,
} as const;
